import React, { useState, useEffect, ReactNode } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Avatar,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  DirectionsBoat as BoatIcon,
  Store as StoreIcon,
  Restaurant as KitchenIcon,
  Business as BusinessIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Settings as SettingsIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setLoggedOutState } from '../utils/auth';
import { useLocationContext } from '../contexts/LocationContext';
import { getAllLocations, BusinessLocation } from '../firebase/services/locationService';

const drawerWidth = 240;

// Removed styled components - using sx props instead

interface MainAdminLayoutProps {
  children: React.ReactNode;
  drawerContent?: ReactNode;
}

const MainAdminLayout: React.FC<MainAdminLayoutProps> = ({ children, drawerContent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const { userProfile, userId, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(null);
  const { setLocation } = useLocationContext();
  const [allLocations, setAllLocations] = useState<BusinessLocation[]>([]);

  // Fetch all locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locs = await getAllLocations();
        setAllLocations(locs);
      } catch (err) {
        // ignore for now
      }
    };
    fetchLocations();
  }, []);

  // Determine current admin section from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/admin/business')) {
      setTabValue(0);
      // Set business location (type: 'Office' or fallback)
      const loc = allLocations.find(l => l.type === 'Office') || allLocations[0];
      if (loc) setLocation(loc);
    } else if (path.includes('/admin/store')) {
      setTabValue(2);
      // Set store location
      const loc = allLocations.find(l => l.type === 'Store');
      if (loc) setLocation(loc);
    } else if (path.includes('/admin/kitchen')) {
      setTabValue(3);
      // Set kitchen location
      const loc = allLocations.find(l => l.type === 'Kitchen');
      if (loc) setLocation(loc);
    } else {
      setTabValue(1); // Marina admin is default
      // Set marina location
      const loc = allLocations.find(l => l.type === 'Marina');
      if (loc) setLocation(loc);
    }
  }, [location.pathname, allLocations, setLocation]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/admin/business');
        // Set business location
        const businessLoc = allLocations.find(l => l.type === 'Office') || allLocations[0];
        if (businessLoc) setLocation(businessLoc);
        break;
      case 1:
        navigate('/admin/dashboard');
        // Set marina location
        const marinaLoc = allLocations.find(l => l.type === 'Marina');
        if (marinaLoc) setLocation(marinaLoc);
        break;
      case 2:
        navigate('/admin/store/dashboard');
        // Set store location
        const storeLoc = allLocations.find(l => l.type === 'Store');
        if (storeLoc) setLocation(storeLoc);
        break;
      case 3:
        navigate('/admin/kitchen/dashboard');
        // Set kitchen location
        const kitchenLoc = allLocations.find(l => l.type === 'Kitchen');
        if (kitchenLoc) setLocation(kitchenLoc);
        break;
      default:
        navigate('/admin/dashboard');
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      setLoggedOutState(); // Immediately flag as logged out
      await signOut();
      window.location.href = '/auth?logout=true';
    } catch (error) {
      console.error('Failed to log out', error);
    }
    handleMenuClose();
  };

  const handleSettings = () => {
    navigate('/admin/settings');
    handleMenuClose();
  };

  // Get current location name for breadcrumb
  const getLocationName = (): string => {
    const path = location.pathname;
    if (path.includes('/admin/store')) return "Store Location";
    if (path.includes('/admin/kitchen')) return "Kitchen Location";
    if (path.includes('/admin/business')) return "Business Admin";
    return "Marina Location";
  };

  // Get location icon for breadcrumb
  const getLocationIcon = () => {
    const path = location.pathname;
    if (path.includes('/admin/store')) return <StoreIcon sx={{ mr: 0.5, fontSize: 18 }} />;
    if (path.includes('/admin/kitchen')) return <KitchenIcon sx={{ mr: 0.5, fontSize: 18 }} />;
    if (path.includes('/admin/business')) return <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />;
    return <BoatIcon sx={{ mr: 0.5, fontSize: 18 }} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(open && {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: `${drawerWidth}px`,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' }, mr: 2 }}>
            <MuiLink 
              component={Link} 
              to="/admin/business" 
              color="inherit" 
              underline="none"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <BusinessIcon sx={{ mr: 1 }} />
              Fallen Leaf Marina
            </MuiLink>
          </Typography>
          
          {!isMobile && (
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              textColor="inherit"
              indicatorColor="secondary"
              sx={{ flexGrow: 1 }}
            >
              <Tab icon={<BusinessIcon />} label="Business Admin" />
              <Tab icon={<BoatIcon />} label="Marina Location" />
              <Tab icon={<StoreIcon />} label="Store Location" />
              <Tab icon={<KitchenIcon />} label="Kitchen Location" />
            </Tabs>
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex' }}>
            {isMobile && (
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
        <Box sx={{ px: 2, py: 1, bgcolor: 'primary.dark', display: 'flex', alignItems: 'center' }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'white' }}>
            <MuiLink 
              component={Link} 
              to="/admin/business" 
              color="inherit" 
              underline="hover"
            >
              Business Admin
            </MuiLink>
            <Typography color="white" sx={{ display: 'flex', alignItems: 'center' }}>
              {getLocationIcon()}
              {getLocationName()}
            </Typography>
          </Breadcrumbs>
        </Box>
      </AppBar>
      
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(0, 1),
          ...theme.mixins.toolbar,
          justifyContent: 'flex-end',
        }}>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>Admin Menu</Typography>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
        <Divider />
        <List>
          {drawerContent}
        </List>
      </Drawer>
      
      {/* Mobile tabs menu */}
      <Menu
        anchorEl={mobileAnchorEl}
        open={Boolean(mobileAnchorEl)}
        onClose={() => setMobileAnchorEl(null)}
        sx={{ mt: '45px' }}
      >
        <MenuItem onClick={() => { 
          handleTabChange({} as React.SyntheticEvent, 0); 
          setMobileAnchorEl(null);
        }}>
          <BusinessIcon sx={{ mr: 1 }} /> Business Admin
        </MenuItem>
        <MenuItem onClick={() => { 
          handleTabChange({} as React.SyntheticEvent, 1); 
          setMobileAnchorEl(null);
        }}>
          <BoatIcon sx={{ mr: 1 }} /> Marina Location
        </MenuItem>
        <MenuItem onClick={() => { 
          handleTabChange({} as React.SyntheticEvent, 2); 
          setMobileAnchorEl(null);
        }}>
          <StoreIcon sx={{ mr: 1 }} /> Store Location
        </MenuItem>
        <MenuItem onClick={() => { 
          handleTabChange({} as React.SyntheticEvent, 3); 
          setMobileAnchorEl(null);
        }}>
          <KitchenIcon sx={{ mr: 1 }} /> Kitchen Location
        </MenuItem>
      </Menu>
      
      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{ mt: '45px' }}
      >
        <MenuItem onClick={handleSettings}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>
      
      <Box component="main" sx={{ 
        flexGrow: 1,
        padding: theme.spacing(3),
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        ...(open && {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        }),
        mt: { xs: '112px', sm: '128px' },
        ml: { xs: 0, md: open ? `${drawerWidth}px` : 0 },
        width: { xs: '100%', md: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default MainAdminLayout; 